import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  CheckSquare,
  Award,
  Grid3X3,
  DollarSign,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
} from 'lucide-react';
import {
  useClientEvents,
  useClientExpenseSummary,
  useClientExpenseItems,
  useUpdateClientApproval,
} from '../hooks/usePortal';
import type { ClientEvent, ClientExpenseItem } from '../api/portal';
import type { ClientApprovalStatus } from '@peles/shared';

function EventCard({
  event,
  onSelect,
  isSelected,
}: {
  event: ClientEvent;
  onSelect: (event: ClientEvent) => void;
  isSelected: boolean;
}) {
  const navigate = useNavigate();
  const dateStr = new Date(event.startAt).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      onClick={() => {
        onSelect(event);
        navigate(`/client/events/${event.id}`);
      }}
      data-testid={`event-card-${event.id}`}
      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <h3 className="font-semibold text-lg">{event.name}</h3>
      <div className="mt-2 space-y-1 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span>{dateStr}</span>
        </div>
        {event.eventTypeName && (
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {event.eventTypeName}
            </span>
          </div>
        )}
        {event.venueName && (
          <div className="flex items-center gap-2">
            <MapPin size={14} />
            <span>{event.venueName}</span>
          </div>
        )}
        {event.guestCount != null && (
          <div className="flex items-center gap-2">
            <Users size={14} />
            <span>{event.guestCount} guests</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              event.status === 'CONFIRMED'
                ? 'bg-green-100 text-green-700'
                : event.status === 'IN_PROGRESS'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-700'
            }`}
          >
            {event.status}
          </span>
        </div>
      </div>
    </button>
  );
}

function ExpenseSummaryCard({ eventId }: { eventId: string }) {
  const { data: summary, isLoading } = useClientExpenseSummary(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading expense summary...</p>;
  if (!summary) return null;

  return (
    <div data-testid="expense-summary" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Budget Summary
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-gray-500">Total Cost</p>
          <p className="text-lg font-bold" data-testid="total-cost">
            ${summary.totalCost.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Paid</p>
          <p className="text-lg font-bold text-green-600" data-testid="total-paid">
            ${summary.totalPaid.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Balance</p>
          <p className="text-lg font-bold text-red-600" data-testid="balance">
            ${summary.balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Items</p>
          <p className="text-lg font-bold">{summary.itemCount}</p>
        </div>
      </div>
    </div>
  );
}

const APPROVAL_BADGE: Record<ClientApprovalStatus, { label: string; className: string }> = {
  PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  APPROVED: { label: 'Approved', className: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
};

function ClientExpenseItemsPanel({ eventId }: { eventId: string }) {
  const { data: items, isLoading } = useClientExpenseItems(eventId);
  const approvalMutation = useUpdateClientApproval();

  if (isLoading) return <p className="text-sm text-gray-500">Loading expense items...</p>;
  if (!items || items.length === 0)
    return <p className="text-sm text-gray-500">No expense items visible for this event.</p>;

  function handleApproval(item: ClientExpenseItem, status: ClientApprovalStatus) {
    approvalMutation.mutate({ expenseItemId: item.id, clientApproval: status });
  }

  return (
    <div data-testid="client-expense-items" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Vendor Selections
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2">Category</th>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">Vendor</th>
              <th className="text-right p-2">Price</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Approval</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const badge = APPROVAL_BADGE[item.clientApproval];
              return (
                <tr key={item.id} className="border-t" data-testid={`client-expense-${item.id}`}>
                  <td className="p-2">{item.vendorCategoryName || '-'}</td>
                  <td className="p-2">{item.itemName || '-'}</td>
                  <td className="p-2">{item.vendorName || '-'}</td>
                  <td className="p-2 text-right">
                    {item.price != null ? `$${item.price.toLocaleString()}` : '-'}
                  </td>
                  <td className="p-2">
                    {item.statusName && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                        {item.statusName}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${badge.className}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleApproval(item, 'APPROVED')}
                        disabled={approvalMutation.isPending}
                        className="p-1 text-gray-400 hover:text-green-600"
                        title="Approve"
                        aria-label="Approve"
                      >
                        <ThumbsUp size={14} />
                      </button>
                      <button
                        onClick={() => handleApproval(item, 'REJECTED')}
                        disabled={approvalMutation.isPending}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Reject"
                        aria-label="Reject"
                      >
                        <ThumbsDown size={14} />
                      </button>
                      {item.clientApproval !== 'PENDING' && (
                        <button
                          onClick={() => handleApproval(item, 'PENDING')}
                          disabled={approvalMutation.isPending}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Reset to Pending"
                          aria-label="Reset to Pending"
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const quickLinks = [
  { label: 'Guest List', icon: Users, path: 'guests' },
  { label: 'Timeline', icon: Clock, path: 'timeline' },
  { label: 'Checklist', icon: CheckSquare, path: 'checklist' },
  { label: 'Kibuddim', icon: Award, path: 'kibuddim' },
  { label: 'Floor Plan', icon: Grid3X3, path: 'floor-plan' },
];

export function ClientDashboardPage() {
  const { data: events, isLoading } = useClientEvents();
  const [selectedEvent, setSelectedEvent] = useState<ClientEvent | null>(null);

  if (isLoading) {
    return <p className="p-4 text-gray-500">Loading your events...</p>;
  }

  if (!events || events.length === 0) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">My Events</h1>
        <p className="text-gray-500">You are not assigned to any events yet.</p>
      </div>
    );
  }

  const budgetVisibility = selectedEvent?.budgetVisibility || 'full';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">My Events</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            onSelect={setSelectedEvent}
            isSelected={selectedEvent?.id === event.id}
          />
        ))}
      </div>

      {selectedEvent && (
        <div className="space-y-4" data-testid="event-detail">
          <h2 className="text-xl font-semibold">{selectedEvent.name}</h2>

          {/* Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {quickLinks.map((link) => (
              <a
                key={link.path}
                href={`/events/${selectedEvent.id}/${link.path}`}
                className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium"
                data-testid={`quick-link-${link.path}`}
              >
                <link.icon size={16} className="text-primary-600" />
                {link.label}
              </a>
            ))}
          </div>

          {/* Expense Summary — only for 'full' or 'summary' visibility */}
          {budgetVisibility !== 'hidden' && <ExpenseSummaryCard eventId={selectedEvent.id} />}

          {/* Expense Items for approval — only for 'full' visibility */}
          {budgetVisibility === 'full' && <ClientExpenseItemsPanel eventId={selectedEvent.id} />}
        </div>
      )}
    </div>
  );
}
